import { type IMessage } from 'react-native-gifted-chat';

type BuildFormatMessageOptions = {
  messages: IMessage[];
  inquiryUserId: number;
  additionalInstructions?: string;
};

/**
 * Builds a formatted message string for the format response API.
 * Separates conversation history from the expert responses with labeled sections.
 *
 * @param options - Options object containing messages, inquiryUserId, and optional additionalInstructions
 * @returns Formatted message string with labeled sections
 */
export function buildFormatMessage(options: BuildFormatMessageOptions): string {
  const { messages, inquiryUserId, additionalInstructions } = options;

  const expertResponses = getLastExpertResponse(messages, inquiryUserId);

  const conversationHistory = getConversationHistory(messages, expertResponses);

  const sections: string[] = [];

  sections.push('--- CONVERSATION ---');

  if (conversationHistory.length > 0) {
    const sortedHistory = [...conversationHistory].reverse();

    sections.push(
      sortedHistory
        .map((msg) => {
          return `${getUserType(msg, inquiryUserId)}:\n${msg.text}`;
        })
        .join('\n\n')
    );
  } else {
    sections.push('(No previous messages)');
  }

  sections.push('\n--- EXPERT RESPONSE ---');

  if (expertResponses.length > 0) {
    const combinedResponses = [...expertResponses]
      .reverse()
      .map((msg) => msg.text)
      .join('\n\n');

    sections.push(combinedResponses || '(Empty response)');
  } else {
    sections.push('(No expert response found)');
  }

  sections.push('\n--- ADDITIONAL INSTRUCTIONS ---');

  sections.push(additionalInstructions?.trim() || 'None');

  return sections.join('\n');
}

function getUserType(message: IMessage, inquiryUserId: number) {
  return Number(message.user._id) === inquiryUserId ? 'Oncologist' : 'Expert';
}

/**
 * Gets the last expert response (all expert messages since the last oncologist message).
 * Messages are expected in newest-first order (from GiftedChat).
 * Expert = any participant who is NOT the inquiry author (oncologist).
 */
export function getLastExpertResponse(messages: IMessage[], inquiryUserId: number): IMessage[] {
  const lastOncologistIndex = messages.findIndex((msg) => Number(msg.user._id) === inquiryUserId);

  if (lastOncologistIndex === -1) {
    return messages.filter((msg) => Number(msg.user._id) !== inquiryUserId);
  }

  if (lastOncologistIndex === 0) {
    return [];
  }

  return messages
    .slice(0, lastOncologistIndex)
    .filter((msg) => Number(msg.user._id) !== inquiryUserId);
}

/**
 * Gets conversation history excluding the expert responses to be formatted.
 */
export function getConversationHistory(
  messages: IMessage[],
  expertResponses: IMessage[]
): IMessage[] {
  if (expertResponses.length === 0) {
    return messages;
  }

  const expertIds = new Set(expertResponses.map((msg) => msg._id));

  return messages.filter((msg) => !expertIds.has(msg._id));
}

/**
 * Checks if there are any expert responses in the conversation.
 */
export function hasExpertResponses(messages: IMessage[], inquiryUserId: number): boolean {
  return messages.some((msg) => Number(msg.user._id) !== inquiryUserId);
}

type BuildFormatMessageFromTextOptions = {
  expertResponseText: string;
  additionalInstructions?: string;
  messages?: IMessage[];
  inquiryUserId?: number;
};

/**
 * Builds a formatted message string for the format response API using editable expert response text.
 * Includes conversation history when messages and inquiryUserId are provided.
 */
export function buildFormatMessageFromText(options: BuildFormatMessageFromTextOptions): string {
  const { expertResponseText, additionalInstructions, messages, inquiryUserId } = options;

  const sections: string[] = [];

  if (messages && messages.length > 0 && inquiryUserId !== undefined) {
    const expertResponses = getLastExpertResponse(messages, inquiryUserId);

    const conversationHistory = getConversationHistory(messages, expertResponses);

    sections.push('--- CONVERSATION ---');

    if (conversationHistory.length > 0) {
      const sortedHistory = [...conversationHistory].reverse();

      sections.push(
        sortedHistory
          .map((msg) => {
            return `${getUserType(msg, inquiryUserId)}:\n${msg.text}`;
          })
          .join('\n\n')
      );
    } else {
      sections.push('(No previous messages)');
    }

    sections.push('');
  }

  sections.push('--- EXPERT RESPONSE ---');

  sections.push(expertResponseText.trim() || '(Empty response)');

  sections.push('\n--- ADDITIONAL INSTRUCTIONS ---');

  sections.push(additionalInstructions?.trim() || 'None');

  return sections.join('\n');
}
